import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { DISCREPANCY_REPORT } from "../../constants/api";
import { EmployeeDetails , ContainerCard } from "../../components/ui/cards";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";

const SEVERITY_BADGE = {
    high: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    low: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

const DiscrepancyReport = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [category, setCategory] = useState("all");
    const navigate = useNavigate();

    const columns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "260px",
            Cell: ({ row }) => {
                const empDetails = {
                    ...row?.employee,
                    designation_id: row?.employee?.designation
                        ? { id: row.employee.designation.id, designation_name: row.employee.designation.name }
                        : { id: 0, designation_name: "" },
                    first_name: row?.employee_name || row?.employee?.employee_name || "-",
                    id: row?.employee_code || row?.employee?.employee_id || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "date",
            Header: "Date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "—"),
        },
        {
            accessor: "category",
            Header: "Category",
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "discrepancy_type",
            Header: "Issue",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "severity",
            Header: "Severity",
            Cell: ({ value }) => (
                <span className={cn("text-xs font-medium px-2 py-0.5 rounded", SEVERITY_BADGE[value] || SEVERITY_BADGE.low)}>
                    {value || "—"}
                </span>
            ),
        },
        {
            accessor: "details",
            Header: "Details",
            Cell: ({ value }) => (
                <span className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{value ?? "-"}</span>
            ),
        },
        {
            accessor: "department",
            Header: "Department",
            Cell: ({ value }) => value ?? "-",
        },
    ];

    return (
        <ContainerCard className="font-sans">
            <div className="flex justify-between items-center mb-4 shrink-0">
                <span className="flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => navigate("/reports")} className="cursor-pointer" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Discrepancy Report ({totalRecords})
                    </h1>
                </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Cross-check attendance, payroll, employee master, and pending salary correction queue.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
                {[
                    { value: "all", label: "All" },
                    { value: "attendance", label: "Attendance" },
                    { value: "payroll", label: "Payroll" },
                    { value: "master", label: "Employee master" },
                    { value: "correction_queue", label: "Salary corrections" },
                ].map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => setCategory(opt.value)}
                        className={cn(
                            "px-3 py-1.5 text-xs rounded-lg border transition-colors",
                            category === opt.value
                                ? "bg-sidebar text-white border-sidebar"
                                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                        )}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={DISCREPANCY_REPORT}
                serverSide={true}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isFromToDateRequired={true}
                filter={{ category }}
                printFilter={{ category }}
            />
        </ContainerCard>
    );
}

export default DiscrepancyReport;
